import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Play, Pause, Sparkles, TrendingUp, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ShareClip = () => {
  const { id } = useParams();
  const videoRef = useRef(null);
  const [clip, setClip] = useState(null);
  const [err, setErr] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/clips/${id}`)
      .then((r) => {
        setClip(r.data);
        document.title = `${r.data.title || 'Clip viral'} · HighlightAI`;
        // OG meta — best effort dynamic update for crawlers that re-fetch
        const m = (prop, content) => {
          let el = document.querySelector(`meta[property="${prop}"]`);
          if (!el) { el = document.createElement('meta'); el.setAttribute('property', prop); document.head.appendChild(el); }
          el.setAttribute('content', content);
        };
        m('og:title', r.data.title || 'Clip viral généré par IA');
        m('og:description', r.data.caption_preview || `Score viral ${r.data.viral_score}/100 · ${r.data.category}`);
        if (r.data.has_thumbnail) m('og:image', `${API}/public/clips/${id}/thumbnail`);
        m('og:type', 'video.other');
        if (r.data.has_file) m('og:video', `${API}/public/clips/${id}/stream`);
        m('og:video:type', 'video/mp4');
      })
      .catch(() => setErr('Clip introuvable'));
  }, [id]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play(); else v.pause();
  };

  if (err) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-black mb-4">404</h1>
          <p className="text-zinc-400 mb-6">{err}</p>
          <Link to="/" className="text-[#D4FF00] hover:underline">← Retour à HighlightAI</Link>
        </div>
      </div>
    );
  }

  if (!clip) {
    return <div className="min-h-screen bg-[#050505] text-zinc-500 flex items-center justify-center">Chargement…</div>;
  }

  const videoUrl = clip.has_file ? `${API}/public/clips/${clip.id}/stream` : null;
  const posterUrl = clip.has_thumbnail ? `${API}/public/clips/${clip.id}/thumbnail` : null;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="share-page">
      {/* Branded header */}
      <header className="border-b border-white/5 px-6 lg:px-12 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[#D4FF00] flex items-center justify-center"><Sparkles className="w-5 h-5 text-black" /></div>
          <span className="font-heading text-xl font-black">HighlightAI</span>
        </Link>
        <Link to="/" className="hidden sm:inline-flex">
          <Button className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold" data-testid="share-cta-create">
            Créer mes propres clips <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-10 lg:py-16 grid lg:grid-cols-12 gap-10 items-center">
        {/* Video preview */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-5">
          <div className="relative mx-auto" style={{ maxWidth: 420 }}>
            <div className="relative aspect-[9/16] overflow-hidden rounded-3xl border border-white/10 shadow-[0_0_60px_-10px_#D4FF0040] bg-black">
              {videoUrl ? (
                <video
                  ref={videoRef}
                  src={videoUrl}
                  poster={posterUrl}
                  className="absolute inset-0 w-full h-full object-cover"
                  playsInline
                  onPlay={() => setPlaying(true)}
                  onPause={() => setPlaying(false)}
                  onClick={togglePlay}
                  data-testid="share-video"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${clip.thumbnail_color}, #050505)` }}>
                  <p className="text-sm text-zinc-400">Aperçu indisponible</p>
                </div>
              )}
              <button
                onClick={togglePlay}
                className={`absolute inset-0 flex items-center justify-center transition-opacity ${playing ? 'opacity-0 hover:opacity-100' : 'opacity-100'} bg-black/20`}
                aria-label={playing ? 'Pause' : 'Play'}
                data-testid="share-playpause"
              >
                <div className="w-20 h-20 rounded-full bg-[#D4FF00] flex items-center justify-center shadow-2xl">
                  {playing ? <Pause className="w-8 h-8 text-black" /> : <Play className="w-8 h-8 text-black ml-1" />}
                </div>
              </button>
              <div className="absolute top-4 right-4 acid-chip px-2.5 py-1 rounded text-[10px] font-bold">{clip.viral_score} VIRAL</div>
            </div>
          </div>
        </motion.div>

        {/* Right: meta */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="lg:col-span-7 space-y-6">
          <div>
            <span className="inline-block text-xs font-mono uppercase tracking-widest text-[#D4FF00] mb-3 px-2.5 py-1 rounded bg-[#D4FF00]/10 border border-[#D4FF00]/30">{clip.category}</span>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-4" data-testid="share-title">
              {clip.title || clip.caption_preview}
            </h1>
            {clip.caption_preview && clip.caption_preview !== clip.title && (
              <p className="text-lg text-zinc-400">{clip.caption_preview}</p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4 p-5 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div>
              <div className="font-heading text-3xl font-black text-[#D4FF00]">{clip.viral_score}</div>
              <div className="text-[10px] uppercase text-zinc-500 mt-1 tracking-wider">Viral</div>
            </div>
            <div>
              <div className="font-heading text-3xl font-black">{clip.engagement_score}</div>
              <div className="text-[10px] uppercase text-zinc-500 mt-1 tracking-wider">Engage</div>
            </div>
            <div>
              <div className="font-heading text-3xl font-black">{clip.retention_score}</div>
              <div className="text-[10px] uppercase text-zinc-500 mt-1 tracking-wider">Retention</div>
            </div>
          </div>

          {clip.keywords?.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="share-keywords">
              {clip.keywords.map((k) => (
                <span key={k} className="px-3 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-zinc-300">#{k}</span>
              ))}
            </div>
          )}

          <div className="p-6 rounded-2xl border border-[#D4FF00]/30 bg-gradient-to-br from-[#D4FF00]/10 to-transparent">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-[#D4FF00]" />
              <span className="font-heading text-xl font-bold">Génère tes propres clips viraux</span>
            </div>
            <p className="text-sm text-zinc-400 mb-4">Upload une vidéo longue, l&apos;IA détecte les meilleurs moments et génère des clips TikTok/Reels/Shorts avec sous-titres synchronisés. Sans CapCut.</p>
            <Link to="/register">
              <Button className="bg-[#D4FF00] text-black hover:bg-[#B3D600] font-semibold w-full sm:w-auto" data-testid="share-cta-register">
                Commencer gratuitement <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </motion.div>
      </main>

      <footer className="border-t border-white/5 py-8 text-center text-xs text-zinc-600">
        Créé avec <Link to="/" className="text-[#D4FF00] hover:underline">HighlightAI</Link> · IA de clips viraux
      </footer>
    </div>
  );
};

export default ShareClip;
